export type EmployeeListFilters = {
  status?: "ACTIVE" | "ON_LEAVE" | "TERMINATED" | "UNKNOWN";
  sort?: "fullName:asc" | "fullName:desc" | "hireDate:asc" | "hireDate:desc";
  limit?: number;
  q?: string;
  companyId?: string;
  departmentId?: string;
  positionId?: string;
  managerId?: string;
};

export const queryKeys = {
  employees: {
    all: ["employees"] as const,
    list: (filters: EmployeeListFilters = {}) =>
      [...queryKeys.employees.all, "list", filters] as const,
    detail: (id: string) => [...queryKeys.employees.all, "detail", id] as const,
    careerHistory: (id: string) =>
      [...queryKeys.employees.all, "career-history", id] as const,
  },
  featureFlags: {
    all: ["feature-flags"] as const,
    map: (tenantId: string) => [...queryKeys.featureFlags.all, "map", tenantId] as const,
    audit: (key?: string) =>
      [...queryKeys.featureFlags.all, "audit", key ?? "all"] as const,
  },
  integrations: {
    all: ["integrations"] as const,
    list: (tenantId: string) =>
      [...queryKeys.integrations.all, "list", tenantId] as const,
  },
  org: {
    all: ["org"] as const,
    companies: () => [...queryKeys.org.all, "companies"] as const,
    company: (id: string) => [...queryKeys.org.all, "company", id] as const,
    departments: (companyId?: string) =>
      [...queryKeys.org.all, "departments", companyId ?? "all"] as const,
    departmentTree: (companyId?: string) =>
      [...queryKeys.org.all, "department-tree", companyId ?? "all"] as const,
    department: (id: string) => [...queryKeys.org.all, "department", id] as const,
    positions: () => [...queryKeys.org.all, "positions"] as const,
    position: (id: string) => [...queryKeys.org.all, "position", id] as const,
  },
  me: {
    all: ["me"] as const,
    dashboard: () => [...queryKeys.me.all, "dashboard"] as const,
    profile: () => [...queryKeys.me.all, "profile"] as const,
    corrections: () => [...queryKeys.me.all, "corrections"] as const,
    payslips: () => [...queryKeys.me.all, "payslips"] as const,
    payslip: (id: string) => [...queryKeys.me.all, "payslip", id] as const,
    vacationBalance: () => [...queryKeys.me.all, "vacation-balance"] as const,
    vacationHistory: () => [...queryKeys.me.all, "vacation-history"] as const,
    vacationRequests: () => [...queryKeys.me.all, "vacation-requests"] as const,
    inboxAll: () => [...queryKeys.me.all, "inbox"] as const,
    inbox: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.me.inboxAll(), "list", filters] as const,
    inboxUnread: () => [...queryKeys.me.inboxAll(), "unread"] as const,
    inboxPreferences: () => [...queryKeys.me.inboxAll(), "preferences"] as const,
    inboxPush: () => [...queryKeys.me.inboxAll(), "push"] as const,
    dataExports: () => [...queryKeys.me.all, "data-exports"] as const,
    requests: () => [...queryKeys.me.all, "requests"] as const,
    request: (id: string) => [...queryKeys.me.all, "request", id] as const,
    benefits: () => [...queryKeys.me.all, "benefits"] as const,
    benefit: (id: string) => [...queryKeys.me.all, "benefit", id] as const,
    documents: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.me.all, "documents", filters] as const,
    document: (id: string) => [...queryKeys.me.all, "document", id] as const,
    announcements: () => [...queryKeys.me.all, "announcements"] as const,
    announcement: (id: string) => [...queryKeys.me.all, "announcement", id] as const,
    timecards: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.me.all, "timecards", filters] as const,
    timecardBalance: () => [...queryKeys.me.all, "timecard-balance"] as const,
  },
  manager: {
    all: ["manager"] as const,
    dashboard: () => [...queryKeys.manager.all, "dashboard"] as const,
    approvals: () => [...queryKeys.manager.all, "approvals"] as const,
    team: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.manager.all, "team", filters] as const,
    vacationCalendar: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.manager.all, "vacation-calendar", filters] as const,
    teamIndicators: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.manager.all, "team-indicators", filters] as const,
  },
  hr: {
    all: ["hr"] as const,
    requests: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.hr.all, "requests", filters] as const,
    request: (id: string) => [...queryKeys.hr.all, "request", id] as const,
    metrics: () => [...queryKeys.hr.all, "metrics"] as const,
    vacations: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.hr.all, "vacations", filters] as const,
    documents: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.hr.all, "documents", filters] as const,
    documentAccesses: (id: string) =>
      [...queryKeys.hr.all, "document-accesses", id] as const,
    announcements: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.hr.all, "announcements", filters] as const,
    announcementReach: (id: string) =>
      [...queryKeys.hr.all, "announcement-reach", id] as const,
    admissions: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.hr.all, "admissions", filters] as const,
    admission: (id: string) => [...queryKeys.hr.all, "admission", id] as const,
    offboardings: (filters: Record<string, unknown> = {}) =>
      [...queryKeys.hr.all, "offboardings", filters] as const,
    offboarding: (id: string) => [...queryKeys.hr.all, "offboarding", id] as const,
  },
};
